import { TestEventBadge } from '../components/TestEventBadge';
import { Link } from 'react-router-dom';
import { YourClub } from '../components/YourClub';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import { statsApi } from '../api/stats';
import { StatTile, Panel, BarChart, Donut, RankedBars, Empty } from '../components/Widgets';

const MONTHS = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const money = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

function eventWhen(e: { eventYear: number; eventMonth?: number | null; eventDate?: string | null }) {
  if (e.eventDate) return new Date(e.eventDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  if (e.eventMonth) return `${MONTHS[e.eventMonth]} ${e.eventYear}`;
  return String(e.eventYear);
}

export function Dashboard() {
  const { user } = useAuth();
  const { data, isLoading, isError } = useQuery({ queryKey: ['stats', 'overview'], queryFn: () => statsApi.overview() });

  if (!user) return null;

  return (
    <div>
      <YourClub />

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gold-400">Dashboard</p>
          <h1 className="mt-2 font-display text-3xl text-cream-50">Club at a glance</h1>
        </div>
        <Link to="/club" className="btn-ghost px-3 py-1.5 text-xs">
          Go to the Club →
        </Link>
      </div>

      {isLoading && <p className="mt-10 text-cream-300/60">Loading the club’s numbers…</p>}
      {isError && <p className="mt-10 text-wine-300">Could not load dashboard data.</p>}

      {data && (
        <div className="mt-8 space-y-6">
          {/* KPI row */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatTile label="Members" value={data.members.total} sub={`${data.members.active} with active logins`} />
            <StatTile label="Tastings Held" value={data.events.held} sub={`${data.events.upcoming} upcoming · ${data.events.live} live`} accent="wine" />
            <StatTile label="Wines Tasted" value={data.winesTasted.toLocaleString()} sub={`${data.scoresRecorded.toLocaleString()} scores recorded`} />
            {data.dues ? (
              <StatTile
                label="Dues Outstanding"
                value={money(data.dues.outstanding)}
                sub={`${data.dues.pendingCount} pending · ${money(data.dues.collected)} collected`}
                accent="wine"
              />
            ) : (
              <StatTile label="Scores Recorded" value={data.scoresRecorded.toLocaleString()} sub="across all tastings" />
            )}
          </div>

          {/* Charts */}
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <Panel title="Tastings per Year">
                <BarChart data={data.tastingsPerYear.map((r) => ({ label: r.year, value: r.count }))} />
              </Panel>
            </div>
            <Panel title="Membership by Type">
              <Donut data={data.membersByType.map((r) => ({ label: r.label, value: r.count }))} />
            </Panel>
          </div>

          <div className="grid gap-6 lg:grid-cols-3">
            {data.avgScoreByYear && data.avgScoreByYear.length > 0 ? (
              <div className="lg:col-span-2">
                <Panel title="Average Wine Score by Year">
                  <BarChart
                    data={data.avgScoreByYear.map((r) => ({
                      label: r.year,
                      value: r.average ?? 0,
                    }))}
                  />
                </Panel>
              </div>
            ) : null}
            {data.winesByColor && data.winesByColor.length > 0 ? (
              <Panel title="Cellar by Colour">
                <Donut data={data.winesByColor.map((r) => ({ label: r.color, value: r.count }))} />
              </Panel>
            ) : null}
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            {data.scoreDistribution && data.scoreDistribution.length > 0 ? (
              <Panel title="Score Distribution">
                <Donut data={data.scoreDistribution.map((r) => ({ label: r.bucket, value: r.count }))} />
              </Panel>
            ) : null}
            {data.topVarietals && data.topVarietals.length > 0 ? (
              <Panel title="Most Tasted Grapes">
                <BarChart data={data.topVarietals.map((r) => ({ label: r.varietal, value: r.count }))} />
              </Panel>
            ) : null}
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <Panel title="Top-Rated Wines">
              <RankedBars
                data={data.topWines.map((w) => {
                  const name = w.wineName || w.producer || `Wine ${w.letter}`;
                  return {
                    label: w.catalogId ? (
                      <Link to={`/cellar/${w.catalogId}`} className="hover:text-gold-200">
                        {name}
                      </Link>
                    ) : (
                      name
                    ),
                    meta: [w.vintage, w.varietal, `${w.eventTitle} ’${String(w.eventYear).slice(2)}`].filter(Boolean).join(' · '),
                    value: w.groupAverage ?? 0,
                    display: w.groupAverage != null ? w.groupAverage.toFixed(2) : '—',
                  };
                })}
              />
            </Panel>

            <Panel
              title="Upcoming Tastings"
              action={
                <Link to="/tastings" className="text-xs text-cream-300/60 hover:text-gold-200">
                  All tastings →
                </Link>
              }
            >
              {data.upcomingEvents.length === 0 ? (
                <Empty>Nothing on the calendar yet.</Empty>
              ) : (
                <ul className="divide-y divide-ink-800">
                  {data.upcomingEvents.map((e) => (
                    <li key={e.id}>
                      <Link to={`/tastings/${e.id}`} className="flex items-center justify-between gap-3 py-3 hover:opacity-80">
                        <div className="min-w-0">
                          <p className="flex items-center gap-2 truncate text-sm text-cream-100">
                            <span className="truncate">{e.title}</span>
                            <TestEventBadge isTest={e.isTest} />
                          </p>
                          <p className="text-xs text-cream-300/50">
                            {eventWhen(e)}
                            {e.regionLocation ? ` · ${e.regionLocation}` : ''}
                          </p>
                        </div>
                        {e.status === 'live' && (
                          <span className="shrink-0 rounded-full bg-wine-700 px-2 py-0.5 text-[10px] uppercase tracking-wide text-on-wine">
                            Live
                          </span>
                        )}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>
          </div>
        </div>
      )}
    </div>
  );
}
